import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import UpInput from '../../components/Common/UpInput';
import UpButton from '../../components/Common/UpButton';
import UpCheckbox from '../../components/Common/UpCheckbox';
import AppPage from './AppPage';
import {
  submitSignupIdentity,
  submitSignupDetails,
  getSignupErrorMessage,
  getSignupRequestId,
} from '../../api/signup';

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
const TOS_URL = 'https://www.urbanpiper.com/legal/merchant-agreement';

// Steps 1+2 merged into ONE screen (owner's one-flow, Aug 6): the server
// still owns two transitions (identity → billing → sign), so Continue
// submits them back-to-back. If the second call fails the server simply
// rests at "billing" and this same screen resumes with everything typed.
function BusinessBillingView({ token, state, onStateChange, onBack }) {
  const [legalEntity, setLegalEntity] = useState(
    state?.identity?.legal_entity || '',
  );
  const [contactName, setContactName] = useState(
    state?.identity?.billing_contact_name ||
      [state?.first_name, state?.last_name].filter(Boolean).join(' '),
  );
  const [contactEmail, setContactEmail] = useState(
    state?.identity?.billing_contact_email || state?.email || '',
  );
  const [street, setStreet] = useState(state?.billing_address?.street || '');
  const [city, setCity] = useState(state?.billing_address?.city || '');
  const [region, setRegion] = useState(state?.billing_address?.state || '');
  const [zip, setZip] = useState(state?.billing_address?.zip || '');
  // Already-recorded agreement stays checked on a resume.
  const [tosAgreed, setTosAgreed] = useState(Boolean(state?.tos_agreed));
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const { isLoading: loading, mutate: saveStep } = useMutation({
    mutationFn: async () => {
      const afterIdentity = await submitSignupIdentity(token, {
        legal_entity: legalEntity.trim(),
        billing_contact_name: contactName.trim(),
        billing_contact_email: contactEmail.trim(),
        // Recorded server-side with a timestamp (tos_agreed_at).
        tos_agreed: tosAgreed,
      });
      if (afterIdentity?.step !== 'billing') {
        return afterIdentity;
      }
      return submitSignupDetails(token, {
        billing_street: street.trim(),
        billing_city: city.trim(),
        billing_state: region.trim(),
        billing_zip: zip.trim(),
      });
    },
    onSuccess: onStateChange,
    onError: setSubmitError,
  });

  function validate() {
    const errors = {};

    if (!legalEntity.trim()) {
      errors.legalEntity = 'Please enter your registered business name';
    }

    if (!contactName.trim()) {
      errors.contactName = 'Please enter the billing contact name';
    }

    if (!EMAIL_REGEX.test(contactEmail.trim())) {
      errors.contactEmail = 'Please enter a valid email address';
    }

    if (!street.trim()) {
      errors.street = 'Please enter your street address';
    }

    if (!city.trim()) {
      errors.city = 'Please enter your city';
    }

    if (!region.trim()) {
      errors.region = 'Please enter your state';
    }

    if (!zip.trim()) {
      errors.zip = 'Please enter your ZIP code';
    }

    if (!tosAgreed) {
      errors.tosAgreed = 'Please agree to the Terms of Service to continue';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function handleSubmit(e) {
    e.preventDefault();
    setSubmitError(null);

    if (loading || !validate()) {
      return;
    }

    saveStep();
  }

  function onFieldChange(field, setter) {
    return value => {
      setSubmitError(null);
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
      setter(value);
    };
  }

  return (
    <AppPage step={state?.step} loading={loading}>
      <h1 className="signup-title">Business &amp; Billing</h1>
      <p className="signup-sub">
        Hi {state?.first_name || 'there'}, let&apos;s set up{' '}
        {state?.business || 'your brand'} on UrbanPiper.
      </p>
      {onBack && (
        <button
          type="button"
          className="text-sm text-up-blue underline -mt-5 mb-5 block"
          onClick={onBack}>
          ← Edit contact details
        </button>
      )}
      <form onSubmit={handleSubmit}>
        <UpInput
          autoFocus
          type="text"
          name="legal_entity"
          label="Legal entity name"
          placeholder="Registered business name on your agreement"
          value={legalEntity}
          onChange={onFieldChange('legalEntity', setLegalEntity)}
          errorContent={fieldErrors.legalEntity}
        />
        <UpInput
          type="text"
          name="billing_contact_name"
          wrapperClass="mt-5"
          label="Billing contact name"
          placeholder="Ex: Jordan Smith"
          value={contactName}
          onChange={onFieldChange('contactName', setContactName)}
          errorContent={fieldErrors.contactName}
        />
        <UpInput
          type="text"
          name="billing_contact_email"
          wrapperClass="mt-5"
          label="Billing contact email"
          placeholder="Ex: billing@example.com"
          helpContent="Invoices go here. Signup emails keep going to your contact email."
          value={contactEmail}
          onChange={onFieldChange('contactEmail', setContactEmail)}
          errorContent={fieldErrors.contactEmail}
        />
        <UpInput
          type="text"
          name="billing_street"
          wrapperClass="mt-5"
          label="Street address"
          placeholder="Ex: 500 Palm Avenue, Suite 12"
          value={street}
          onChange={onFieldChange('street', setStreet)}
          errorContent={fieldErrors.street}
        />
        <div className="flex gap-4">
          <UpInput
            type="text"
            name="billing_city"
            wrapperClass="mt-5 flex-1"
            label="City"
            placeholder="Ex: Miami"
            value={city}
            onChange={onFieldChange('city', setCity)}
            errorContent={fieldErrors.city}
          />
          <UpInput
            type="text"
            name="billing_state"
            wrapperClass="mt-5 flex-1"
            label="State"
            placeholder="Ex: FL"
            value={region}
            onChange={onFieldChange('region', setRegion)}
            errorContent={fieldErrors.region}
          />
        </div>
        <UpInput
          type="text"
          name="billing_zip"
          wrapperClass="mt-5"
          label="ZIP code"
          placeholder="Ex: 33101"
          value={zip}
          onChange={onFieldChange('zip', setZip)}
          errorContent={fieldErrors.zip}
        />
        <UpCheckbox
          name="tos_agreed"
          wrapperClass="mt-5 mb-8"
          value={tosAgreed}
          onChange={onFieldChange('tosAgreed', setTosAgreed)}
          errorContent={fieldErrors.tosAgreed}
          label={
            <span>
              By signing up, I agree to the UrbanPiper{' '}
              <a
                href={TOS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-up-blue underline"
                onClick={e => e.stopPropagation()}>
                Terms of Service
              </a>
              .
            </span>
          }
        />
        {submitError && (
          <div className="text-sm text-up-clr-semantic-red break-words mb-6">
            {getSignupErrorMessage(submitError)}
            {getSignupRequestId(submitError) && (
              <span className="block text-xs text-up-clr-light-grey mt-1">
                Reference: {getSignupRequestId(submitError)}
              </span>
            )}
          </div>
        )}
        <UpButton type="submit" disabled={loading}>
          {loading ? 'Saving…' : 'Continue'}
        </UpButton>
        <p className="signup-footnote">
          Your order form opens for signing in the next step.
        </p>
      </form>
    </AppPage>
  );
}

export default BusinessBillingView;
